import React, { useMemo } from "react";
import { CustomDropdown, fieldLabelClasses } from "../../../components/ui";
import { useClients } from "../hooks/useClients";
import { fullNameOf } from "../clientMapping";

export interface ClientPickerFieldProps {
  /** Prefixes the label's id, so two pickers on one screen stay apart. */
  id: string;
  label: string;
  /** The picked client's `public_id`, or "" for none yet. */
  clientId: string;
  /** Given the id and the name beside it, so a caller need not hold the list. */
  onChange: (clientId: string, name: string) => void;
}

/**
 * A searchable dropdown over the workspace's active clients.
 *
 * The first step of anything written *for* someone — a meal plan, a supplement
 * plan, a day of exercise — when the screen it is written from does not already
 * know who. A client's own screen skips it; the Create plan screen cannot.
 *
 * Active clients only — the same list as My clients' Active tab, ending-soon
 * included. A plan is written for somebody on one; an old client whose plan
 * has run out, or a new one who has not bought anything yet, is not who any
 * of the three builders is for, and offering them here only made the list
 * longer to search.
 *
 * Its own component so `useClients` is mounted with it: a dialog opened from a
 * client's screen never shows this step, and a hook called anyway would fetch
 * the list to never read it — and toast a failure for a list the flow does
 * not need.
 */
const ClientPickerField: React.FC<ClientPickerFieldProps> = ({
  id,
  label,
  clientId,
  onChange,
}) => {
  const { clients, status } = useClients("ACTIVE");

  const options = useMemo(
    () =>
      clients.map((record) => ({
        value: record.publicId,
        label: fullNameOf(record),
      })),
    [clients]
  );

  const placeholder =
    status === "loading"
      ? "Loading clients…"
      : status === "failed"
      ? "Could not load your clients"
      : "Search client";

  const labelId = `${id}-label`;

  return (
    <div>
      <label id={labelId} className={`${fieldLabelClasses} mb-1.5`}>
        {label}
      </label>
      {/* `CustomDropdown` rather than `DropdownField`: the row has to carry the
          client's id, and the labelled field only deals in strings — two
          clients can share a name, an id cannot. */}
      <CustomDropdown
        options={options}
        value={clientId}
        onChange={(value) =>
          onChange(
            value,
            options.find((option) => option.value === value)?.label ?? ""
          )
        }
        placeholder={placeholder}
        disabled={status !== "ready"}
        // Said in the open list rather than the placeholder: the field reads
        // "Search client" until it is opened, and opening an empty list is the
        // moment someone wants to know why there is nothing to search.
        emptyMessage="No active clients yet. Only clients on an active plan can be given one."
        searchable
        aria-labelledby={labelId}
      />
    </div>
  );
};

export default ClientPickerField;
